import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FrequencyMethodRow from "./frequencyMethodRow";
import {
  OrderNotificationFrequencyType,
  OrderNotificationPreferenceType,
  OrderNotificationResponse,
  OrderNotificationSettingRequest,
  OrderNotificationSubscriptionSettingModel,
  OrderNotificationSubscriptionType,
} from "@/app/models/orderNotification/types";
import dataService from "@/app/services/dataService";
import { useGlobalNotification } from "@/app/components/header/GlobalNotificationContext";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store/store";
import { Lookup } from "@/app/models/common/types";
import { AddressDropdownItem, OptionGroup } from "@/app/models/checkout/types";
import CheckIcon from "@mui/icons-material/Check";
import CommonHelper from "@/app/helpers/CommonHelper";
export enum MethodChoice {
  Email,
  Text,
  Both,
}

const toMethodChoice = (isEmail: boolean, isPhone: boolean): MethodChoice => {
  if (isEmail && isPhone) return MethodChoice.Both;
  if (isEmail) return MethodChoice.Email;
  if (isPhone) return MethodChoice.Text;
  return MethodChoice.Email;
};
const fromMethodChoice = (m: MethodChoice) => ({
  isEmailMethod: m === MethodChoice.Email || m === MethodChoice.Both,
  isPhoneMethod: m === MethodChoice.Text || m === MethodChoice.Both,
});

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

const serialize = (p: OrderNotificationSettingRequest) => {
  const emailsArr = Array.isArray(p.email) ? p.email : [];
  const cleaned = emailsArr
    .map((e) => (e || "").trim().toLowerCase())
    .filter(Boolean)
    .sort();
  const normalizedEmails: string[] = [];
  for (let i = 0; i < cleaned.length; i++) {
    if (i === 0 || cleaned[i] !== cleaned[i - 1])
      normalizedEmails.push(cleaned[i]);
  }

  const subsArr = Array.isArray(p.subscriptionSettings)
    ? p.subscriptionSettings.slice()
    : [];
  subsArr.sort((a, b) => {
    const nt = String(a.notificationType).localeCompare(
      String(b.notificationType)
    );
    if (nt !== 0) return nt;
    const fr = String(a.frequency).localeCompare(String(b.frequency));
    if (fr !== 0) return fr;
    const em = Number(a.isEmailMethod) - Number(b.isEmailMethod);
    if (em !== 0) return em;
    return Number(a.isPhoneMethod) - Number(b.isPhoneMethod);
  });

  const normalized: OrderNotificationSettingRequest = {
    subscribed: !!p.subscribed,
    optedOutWarning: p.optedOutWarning || "",
    hasPromotions: !!p.hasPromotions,
    hasIndustryUpdates: !!p.hasIndustryUpdates,
    contactByEmail: !!p.contactByEmail,
    contactByText: !!p.contactByText,
    email: normalizedEmails,
    phone: CommonHelper.normalizedPhone(p.phone || ""),
    subscriptionType: p.subscriptionType,
    subscriptionSettings: subsArr,
    shipToSettings: p.shipToSettings,
  };

  return JSON.stringify(normalized);
};

interface OrderNotificationPreferencesProps {
  model?: OrderNotificationResponse | null;
  isAccount?: boolean;
  showHeadings?: boolean;
  className?: string;
  manageUserId?: number;
}

export interface OrderNotificationPreferencesHandle {
  save: () => Promise<void>;
}

const OrderNotificationPreferencesInner = (
  {
    model,
    isAccount,
    showHeadings,
    className,
    manageUserId,
  }: OrderNotificationPreferencesProps,
  ref: React.Ref<OrderNotificationPreferencesHandle | null>
) => {
  const loggedInUserId = useSelector(
    (state: RootState) => state.userState.user?.id ?? 0
  );

  const userId = manageUserId ?? loggedInUserId;

  const customerNumber = useSelector(
    (state: RootState) => state.userState.user?.customerNumber ?? null
  );
  const [selectedShipTos, setSelectedShipTos] = useState<AddressDropdownItem[]>(
    model?.shipToSettings.map((x) => ({
      id: x.shipTo,
      name: x.shipToName,
    })) ?? []
  );
  const [shipToloading, setShipToLoading] = useState(true);
  const [customerShipTos, setCustomerShipTos] = useState<OptionGroup[]>([]);

  const [loading, setLoading] = useState(false);
  const { setNotification } = useGlobalNotification();
  const [isSubscribed] = useState<boolean>(!!model?.subscribed);
  const [selectedVals, setSelectedVals] = useState<string[]>([]);
  const [aboutOrders, setAboutOrders] = useState<boolean>(false);
  const [aboutDeliveries, setAboutDeliveries] = useState<boolean>(false);
  const [hasPromotions, setHasPromotions] = useState<boolean>(
    model?.hasPromotions ?? false
  );
  const [hasIndustryUpdates, setHasIndustryUpdates] = useState<boolean>(
    model?.hasIndustryUpdates ?? false
  );

  const [viaEmail, setViaEmail] = useState<boolean>(
    model?.contactByEmail ?? false
  );
  const [viaText, setViaText] = useState<boolean>(
    model?.contactByText ?? false
  );
  const [emails, setEmails] = useState<string[]>(
    model?.email?.length ? [...model.email] : [""]
  );

  const [phone, setPhone] = useState<string>(
    model?.phone
      ? CommonHelper.formatPhone334((model.phone ?? "").toString())
      : ""
  );

  const [subscriptionType, setSubscriptionType] =
    useState<OrderNotificationSubscriptionType>(
      model?.subscriptionType ??
        OrderNotificationSubscriptionType.RecommendedNotifications
    );

  const [showCustomize, setShowCustomize] = useState<boolean>(true);

  const [ackEach, setAckEach] = useState<boolean>(false);
  const [ackEachMethod, setAckEachMethod] = useState<MethodChoice>(
    MethodChoice.Email
  );
  const [ackDaily, setAckDaily] = useState<boolean>(false);
  const [ackDailyMethod, setAckDailyMethod] = useState<MethodChoice>(
    MethodChoice.Email
  );

  const [readyEach, setReadyEach] = useState<boolean>(false);
  const [readyEachMethod, setReadyEachMethod] = useState<MethodChoice>(
    MethodChoice.Email
  );
  const [readyDaily, setReadyDaily] = useState<boolean>(false);
  const [readyDailyMethod, setReadyDailyMethod] = useState<MethodChoice>(
    MethodChoice.Email
  );
  const [delOutFor, setDelOutFor] = useState<boolean>(false);
  const [delOutForMethod, setDelOutForMethod] = useState<MethodChoice>(
    MethodChoice.Email
  );
  const [delComplete, setDelComplete] = useState<boolean>(false);
  const [delCompleteMethod, setDelCompleteMethod] = useState<MethodChoice>(
    MethodChoice.Email
  );
  const [delDaily, setDelDaily] = useState<boolean>(false);
  const [delDailyMethod, setDelDailyMethod] = useState<MethodChoice>(
    MethodChoice.Email
  );

  const handleContinueShopping = () => {
    window.location.href = "/lbpcustomer";
  };

  const [isDirty, setIsDirty] = useState(false);
  const baselineRef = React.useRef<string>("");

  const [errors, setErrors] = useState<string[]>([]);

  const setPhoneAt = (val: string) => {
    const formatted = CommonHelper.formatPhone334(val);
    setPhone(formatted);
  };

  const onSave = async (payload: OrderNotificationSettingRequest) => {
    try {
      setLoading(true);
      var res = dataService.updateOrderNotifications(payload, userId);
      if (!(await res).success) {
        setNotification("Failed to save preferences.", "error");
        return;
      } else {
        setNotification(<>Your preferences have been saved.</>, "success");
        baselineRef.current = serialize(payload);
        setIsDirty(false);
      }
    } catch (e: any) {
      setNotification("Failed to save preferences.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const get = (
      n: OrderNotificationPreferenceType,
      f: OrderNotificationFrequencyType
    ) =>
      model?.subscriptionSettings.find(
        (s) => s.notificationType === n && s.frequency === f
      );

    setAboutOrders(
      !!model?.subscriptionSettings.find(
        (s) =>
          s.notificationType ===
            OrderNotificationPreferenceType.Acknowledgements ||
          s.notificationType === OrderNotificationPreferenceType.OrderReady
      )
    );
    setAboutDeliveries(
      !!model?.subscriptionSettings.find(
        (s) =>
          s.notificationType === OrderNotificationPreferenceType.DeliveryStatus
      )
    );

    {
      const each = get(
        OrderNotificationPreferenceType.Acknowledgements,
        OrderNotificationFrequencyType.EachOrder
      );
      setAckEach(!!each);
      setAckEachMethod(
        each
          ? toMethodChoice(each.isEmailMethod, each.isPhoneMethod)
          : MethodChoice.Email
      );

      const daily = get(
        OrderNotificationPreferenceType.Acknowledgements,
        OrderNotificationFrequencyType.DailySummary
      );
      setAckDaily(!!daily);
      setAckDailyMethod(
        daily
          ? toMethodChoice(daily.isEmailMethod, daily.isPhoneMethod)
          : MethodChoice.Email
      );
    }

    {
      const each = get(
        OrderNotificationPreferenceType.OrderReady,
        OrderNotificationFrequencyType.EachOrder
      );
      setReadyEach(!!each);
      setReadyEachMethod(
        each
          ? toMethodChoice(each.isEmailMethod, each.isPhoneMethod)
          : MethodChoice.Email
      );

      const daily = get(
        OrderNotificationPreferenceType.OrderReady,
        OrderNotificationFrequencyType.DailySummary
      );
      setReadyDaily(!!daily);
      setReadyDailyMethod(
        daily
          ? toMethodChoice(daily.isEmailMethod, daily.isPhoneMethod)
          : MethodChoice.Email
      );
    }

    {
      const outFor = get(
        OrderNotificationPreferenceType.DeliveryStatus,
        OrderNotificationFrequencyType.OutForDelivery
      );
      setDelOutFor(!!outFor);
      setDelOutForMethod(
        outFor
          ? toMethodChoice(outFor.isEmailMethod, outFor.isPhoneMethod)
          : MethodChoice.Email
      );

      const complete = get(
        OrderNotificationPreferenceType.DeliveryStatus,
        OrderNotificationFrequencyType.DeliveryComplete
      );
      setDelComplete(!!complete);
      setDelCompleteMethod(
        complete
          ? toMethodChoice(complete.isEmailMethod, complete.isPhoneMethod)
          : MethodChoice.Email
      );

      const daily = get(
        OrderNotificationPreferenceType.DeliveryStatus,
        OrderNotificationFrequencyType.DailyDelivery
      );
      setDelDaily(!!daily);
      setDelDailyMethod(
        daily
          ? toMethodChoice(daily.isEmailMethod, daily.isPhoneMethod)
          : MethodChoice.Email
      );
    }

    fetchAllCustomerShipTos();

    baselineRef.current = serialize(buildPayloadFromModel(model ?? null));
  }, [model]);

  useEffect(() => {
    const snap = serialize(buildPayload());
    setIsDirty(snap !== baselineRef.current);
  }, [
    hasPromotions,
    hasIndustryUpdates,
    viaEmail,
    viaText,
    emails,
    phone,
    subscriptionType,
    aboutOrders,
    aboutDeliveries,
    ackEach,
    ackEachMethod,
    ackDaily,
    ackDailyMethod,
    readyEach,
    readyEachMethod,
    readyDaily,
    readyDailyMethod,
    delOutFor,
    delOutForMethod,
    delComplete,
    delCompleteMethod,
    delDaily,
    delDailyMethod,
    selectedShipTos,
  ]);

  const fetchAllCustomerShipTos = async () => {
    try {
      setShipToLoading(true);
      const response = await dataService.getAllCustomerShipTos(customerNumber);
      const shipTos: Lookup[] = response.data ?? [];
      if (shipTos.length > 0) {
        const grouped: OptionGroup[] = [
          {
            label: "Addresses",
            options: shipTos.map((addr: Lookup) => ({
              id: addr.value,
              name: addr.displayName,
            })),
          },
        ];

        setCustomerShipTos(grouped);
      }
    } catch (error) {
      console.error("Failed to load templates:", error);
    } finally {
      setShipToLoading(false);
    }
  };

  const hasAnyDeliveryFreq = delOutFor || delComplete || delDaily;
  const hasAnyAckOrReady = ackEach || ackDaily || readyEach || readyDaily;

  const methodFromContact = (
    emailOn: boolean,
    textOn: boolean
  ): MethodChoice => {
    if (emailOn && textOn) return MethodChoice.Both;
    if (emailOn) return MethodChoice.Email;
    if (textOn) return MethodChoice.Text;
    return MethodChoice.Email;
  };

  const UpdateCheckBoxes = (
    overrides?: Partial<{
      aboutOrders: boolean;
      aboutDeliveries: boolean;
      subscriptionType: OrderNotificationSubscriptionType;
      viaEmail: boolean;
      viaText: boolean;
    }>
  ) => {
    const nextAboutOrders = overrides?.aboutOrders ?? aboutOrders;
    const nextAboutDeliveries = overrides?.aboutDeliveries ?? aboutDeliveries;
    const nextSub = overrides?.subscriptionType ?? subscriptionType;
    const nextViaEmail = overrides?.viaEmail ?? viaEmail;
    const nextViaText = overrides?.viaText ?? viaText;

    const m = methodFromContact(nextViaEmail, nextViaText);
    const noContact = !nextViaEmail && !nextViaText;
    if (noContact) {
      setAckEach(false);
      setAckEachMethod(MethodChoice.Email);
      setAckDaily(false);
      setAckDailyMethod(MethodChoice.Email);
      setReadyEach(false);
      setReadyEachMethod(MethodChoice.Email);
      setReadyDaily(false);
      setReadyDailyMethod(MethodChoice.Email);

      setDelOutFor(false);
      setDelOutForMethod(MethodChoice.Email);
      setDelComplete(false);
      setDelCompleteMethod(MethodChoice.Email);
      setDelDaily(false);
      setDelDailyMethod(MethodChoice.Email);
      return;
    }

    if (nextAboutOrders) {
      if (
        nextSub ===
          OrderNotificationSubscriptionType.RecommendedNotifications ||
        nextSub === OrderNotificationSubscriptionType.EachOrder
      ) {
        setAckEach(true);
        setAckEachMethod(m);
        setAckDaily(false);
        setAckDailyMethod(MethodChoice.Email);

        setReadyEach(true);
        setReadyEachMethod(m);
        setReadyDaily(false);
        setReadyDailyMethod(MethodChoice.Email);
      }

      if (nextSub === OrderNotificationSubscriptionType.DailySummary) {
        setAckEach(false);
        setAckEachMethod(MethodChoice.Email);
        setAckDaily(true);
        setAckDailyMethod(m);

        setReadyEach(false);
        setReadyEachMethod(MethodChoice.Email);
        setReadyDaily(true);
        setReadyDailyMethod(m);
      }
    }

    if (!nextAboutDeliveries) return;

    if (nextSub === OrderNotificationSubscriptionType.DailySummary) {
      setDelOutFor(false);
      setDelOutForMethod(MethodChoice.Email);
      setDelComplete(false);
      setDelCompleteMethod(MethodChoice.Email);
      setDelDaily(true);
      setDelDailyMethod(m);
    } else if (
      nextSub === OrderNotificationSubscriptionType.RecommendedNotifications
    ) {
      setDelOutFor(false);
      setDelOutForMethod(MethodChoice.Email);
      setDelComplete(true);
      setDelCompleteMethod(m);
      setDelDaily(false);
      setDelDailyMethod(MethodChoice.Email);
    } else {
      setDelOutFor(true);
      setDelOutForMethod(m);
      setDelComplete(true);
      setDelCompleteMethod(m);
      setDelDaily(false);
      setDelDailyMethod(MethodChoice.Email);
    }
  };

  const validate = (): string[] => {
    const errs: string[] = [];

    if (viaEmail) {
      const bad = emails
        .map((e) => e.trim())
        .filter((e) => e.length > 0 && !emailRegex.test(e));
      if (bad.length) errs.push("Invalid email address.");
      if (!emails.some((e) => e.trim().length > 0))
        errs.push(
          "At least one email is required to receive Email notifications."
        );
    }

    if (viaText) {
      if (!phone || CommonHelper.normalizedPhone(phone).length != 10)
        errs.push(
          "Field 'Text' is required to receive notifications by Phone."
        );
    }

    const deliveries = [
      { on: ackEach, method: ackEachMethod },
      { on: ackDaily, method: ackDailyMethod },
      { on: readyEach, method: readyEachMethod },
      { on: readyDaily, method: readyDailyMethod },
      { on: delOutFor, method: delOutForMethod },
      { on: delComplete, method: delCompleteMethod },
      { on: delDaily, method: delDailyMethod },
    ];

    const needsEmail = deliveries.some(
      (d) =>
        d.on &&
        (d.method === MethodChoice.Email || d.method === MethodChoice.Both)
    );
    if (needsEmail && !viaEmail) {
      errs.push(
        "Field 'Email' is required if you want to receive notifications by Email."
      );
    }

    const needsText = deliveries.some(
      (d) =>
        d.on &&
        (d.method === MethodChoice.Text || d.method === MethodChoice.Both)
    );
    if (needsText && !viaText) {
      errs.push(
        "Field 'Text' is required if you want to receive notifications by Phone."
      );
    }

    if (aboutOrders && !hasAnyAckOrReady) {
      errs.push("Choose at least one frequency for Orders.");
    }
    if (aboutDeliveries && !hasAnyDeliveryFreq) {
      errs.push("Choose at least one frequency for Deliveries.");
    }

    return errs;
  };

  const buildPayload = (): OrderNotificationSettingRequest => {
    const sub: OrderNotificationSubscriptionSettingModel[] = [];

    const pushIf = (
      on: boolean,
      notificationType: OrderNotificationPreferenceType,
      frequency: OrderNotificationFrequencyType,
      method: MethodChoice
    ) => {
      if (!on) return;
      const m = fromMethodChoice(method);
      sub.push({
        notificationType,
        frequency,
        isEmailMethod: m.isEmailMethod,
        isPhoneMethod: m.isPhoneMethod,
      });
    };

    if (aboutOrders) {
      pushIf(
        ackEach,
        OrderNotificationPreferenceType.Acknowledgements,
        OrderNotificationFrequencyType.EachOrder,
        ackEachMethod
      );
      pushIf(
        ackDaily,
        OrderNotificationPreferenceType.Acknowledgements,
        OrderNotificationFrequencyType.DailySummary,
        ackDailyMethod
      );
      pushIf(
        readyEach,
        OrderNotificationPreferenceType.OrderReady,
        OrderNotificationFrequencyType.EachOrder,
        readyEachMethod
      );
      pushIf(
        readyDaily,
        OrderNotificationPreferenceType.OrderReady,
        OrderNotificationFrequencyType.DailySummary,
        readyDailyMethod
      );
    }

    if (aboutDeliveries) {
      pushIf(
        delOutFor,
        OrderNotificationPreferenceType.DeliveryStatus,
        OrderNotificationFrequencyType.OutForDelivery,
        delOutForMethod
      );
      pushIf(
        delComplete,
        OrderNotificationPreferenceType.DeliveryStatus,
        OrderNotificationFrequencyType.DeliveryComplete,
        delCompleteMethod
      );
      pushIf(
        delDaily,
        OrderNotificationPreferenceType.DeliveryStatus,
        OrderNotificationFrequencyType.DailyDelivery,
        delDailyMethod
      );
    }

    return {
      subscribed: model?.subscribed ?? false,
      optedOutWarning: model?.optedOutWarning ?? "",
      hasPromotions,
      hasIndustryUpdates,
      contactByEmail: viaEmail,
      contactByText: viaText,
      email: emails.map((e) => e.trim()).filter(Boolean),
      phone: viaText ? CommonHelper.normalizedPhone(phone) : "",
      subscriptionType,
      subscriptionSettings: sub,
      shipToSettings:
        selectedShipTos.map((x) => ({
          shipTo: x.id,
          shipToName: x.name,
        })) ?? [],
    };
  };

  const buildPayloadFromModel = (
    m: OrderNotificationResponse | null
  ): OrderNotificationSettingRequest => {
    const contactByEmail = m?.contactByEmail ?? false;
    const contactByText = m?.contactByText ?? false;

    const methodFlags = fromMethodChoice(
      contactByEmail && contactByText
        ? MethodChoice.Both
        : contactByEmail
        ? MethodChoice.Email
        : contactByText
        ? MethodChoice.Text
        : MethodChoice.Email
    );

    const subType =
      m?.subscriptionType ??
      OrderNotificationSubscriptionType.RecommendedNotifications;
    const subs: OrderNotificationSubscriptionSettingModel[] = [];

    var orders = !!model?.subscriptionSettings.find(
      (s) =>
        s.notificationType ===
          OrderNotificationPreferenceType.Acknowledgements ||
        s.notificationType === OrderNotificationPreferenceType.OrderReady
    );

    var deliveries = !!model?.subscriptionSettings.find(
      (s) =>
        s.notificationType === OrderNotificationPreferenceType.DeliveryStatus
    );

    if (orders) {
      var ackEach = model?.subscriptionSettings.some(
        (s) =>
          s.notificationType ===
            OrderNotificationPreferenceType.Acknowledgements &&
          s.frequency === OrderNotificationFrequencyType.EachOrder
      );

      var ackDaily = model?.subscriptionSettings.some(
        (s) =>
          s.notificationType ===
            OrderNotificationPreferenceType.Acknowledgements &&
          s.frequency === OrderNotificationFrequencyType.DailySummary
      );

      var orderReady = model?.subscriptionSettings.some(
        (s) =>
          s.notificationType === OrderNotificationPreferenceType.OrderReady &&
          s.frequency === OrderNotificationFrequencyType.EachOrder
      );

      var orderDaily = model?.subscriptionSettings.some(
        (s) =>
          s.notificationType === OrderNotificationPreferenceType.OrderReady &&
          s.frequency === OrderNotificationFrequencyType.DailySummary
      );

      if (ackEach) {
        subs.push({
          notificationType: OrderNotificationPreferenceType.Acknowledgements,
          frequency: OrderNotificationFrequencyType.EachOrder,
          isEmailMethod: methodFlags.isEmailMethod,
          isPhoneMethod: methodFlags.isPhoneMethod,
        });
      }
      if (ackDaily) {
        subs.push({
          notificationType: OrderNotificationPreferenceType.Acknowledgements,
          frequency: OrderNotificationFrequencyType.DailySummary,
          isEmailMethod: methodFlags.isEmailMethod,
          isPhoneMethod: methodFlags.isPhoneMethod,
        });
      }
      if (orderReady) {
        subs.push({
          notificationType: OrderNotificationPreferenceType.OrderReady,
          frequency: OrderNotificationFrequencyType.EachOrder,
          isEmailMethod: methodFlags.isEmailMethod,
          isPhoneMethod: methodFlags.isPhoneMethod,
        });
      }
      if (orderDaily) {
        subs.push({
          notificationType: OrderNotificationPreferenceType.OrderReady,
          frequency: OrderNotificationFrequencyType.DailySummary,
          isEmailMethod: methodFlags.isEmailMethod,
          isPhoneMethod: methodFlags.isPhoneMethod,
        });
      }
    }

    if (deliveries) {
      var outDelivery = model?.subscriptionSettings.some(
        (s) =>
          s.notificationType ===
            OrderNotificationPreferenceType.DeliveryStatus &&
          s.frequency === OrderNotificationFrequencyType.OutForDelivery
      );

      var deliveryComplete = model?.subscriptionSettings.some(
        (s) =>
          s.notificationType ===
            OrderNotificationPreferenceType.DeliveryStatus &&
          s.frequency === OrderNotificationFrequencyType.DeliveryComplete
      );

      var deliveryDaily = model?.subscriptionSettings.some(
        (s) =>
          s.notificationType ===
            OrderNotificationPreferenceType.DeliveryStatus &&
          s.frequency === OrderNotificationFrequencyType.DailyDelivery
      );

      if (outDelivery) {
        subs.push({
          notificationType: OrderNotificationPreferenceType.DeliveryStatus,
          frequency: OrderNotificationFrequencyType.OutForDelivery,
          isEmailMethod: methodFlags.isEmailMethod,
          isPhoneMethod: methodFlags.isPhoneMethod,
        });
      }
      if (deliveryComplete) {
        subs.push({
          notificationType: OrderNotificationPreferenceType.DeliveryStatus,
          frequency: OrderNotificationFrequencyType.DeliveryComplete,
          isEmailMethod: methodFlags.isEmailMethod,
          isPhoneMethod: methodFlags.isPhoneMethod,
        });
      }
      if (deliveryDaily) {
        subs.push({
          notificationType: OrderNotificationPreferenceType.DeliveryStatus,
          frequency: OrderNotificationFrequencyType.DailyDelivery,
          isEmailMethod: methodFlags.isEmailMethod,
          isPhoneMethod: methodFlags.isPhoneMethod,
        });
      }
    }

    return {
      subscribed: m?.subscribed ?? false,
      optedOutWarning: m?.optedOutWarning ?? "",
      hasPromotions: m?.hasPromotions ?? false,
      hasIndustryUpdates: m?.hasIndustryUpdates ?? false,
      contactByEmail,
      contactByText,
      email: (m?.email ?? []).map((e) => (e ?? "").trim()).filter(Boolean),
      phone: viaText ? CommonHelper.normalizedPhone(m?.phone ?? "") : "",
      subscriptionType: subType,
      subscriptionSettings: subs,
      shipToSettings: m?.shipToSettings ?? [],
    };
  };

  const onChangeSubscriptionType = (v: OrderNotificationSubscriptionType) => {
    setSubscriptionType(v);
    UpdateCheckBoxes({ subscriptionType: v });
  };

  const addEmail = () => setEmails((prev) => [...prev, ""]);
  const removeEmailAt = (idx: number) => {
    setEmails((prev) => {
      if (prev.length <= 1) {
        return prev;
      }
      return prev.filter((_, i) => i !== idx);
    });
  };

  const updateEmailAt = (idx: number, val: string) =>
    setEmails((prev) => prev.map((e, i) => (i === idx ? val : e)));

  const save = async () => {
    const errs = validate();
    setErrors(errs);
    if (errs.length || !isDirty) return;
    await onSave(buildPayload());
  };

  useImperativeHandle(ref, () => ({
    save,
  }));

  return (
    <>
      {loading ? (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1800,
            pointerEvents: "auto",
          }}
        >
          <CircularProgress size={40} sx={{ color: "#1976d2" }} />
        </Box>
      ) : (
        <></>
      )}
      <Paper
        className={`orderNotification ${className ?? ""}`}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          width: { xs: "80%", sm: "80%", md: "60%", lg: "50%" },
          mx: "auto",
          borderRadius: 2,
        }}
      >
        {showHeadings && !isSubscribed && !isAccount && (
          <Typography sx={{ fontWeight: "700", mb: 3, textAlign: "center" }}>
            You are not currently enrolled in Order Notifications.
          </Typography>
        )}

        {showHeadings && !isAccount && (
          <>
            <Typography sx={{ mb: 3, textAlign: "center" }}>
              Want to stay in the loop on your future orders? With LansingNOW,
              you have the ability to receive email and SMS notifications on
              your orders, returns, delivery status, and if an order is ready at
              your local branch! Please enroll below to begin receiving these
              notifications.
            </Typography>
          </>
        )}

        {showHeadings && isAccount && (
          <>
            <Typography sx={{ mb: 4 }}>
              From the warehouse to the jobsite, you’re in the know with order
              acknowledgments, real-time alerts, and image verification of
              completed deliveries. It’s one more way we deliver excellence
              every day.
            </Typography>
          </>
        )}

        {showHeadings && !isSubscribed && (
          <>
            <Typography
              sx={{
                fontStyle: "italic",
                fontSize: "14px",
                color: "red",
                mb: 3,
              }}
            >
              You are not currently enrolled in Notifications. To enroll, please
              make changes to the form below and "Update Preferences" to save
              before leaving the page.
            </Typography>
          </>
        )}

        <Typography className="notification-subTitle">
          Contact Me About:
        </Typography>

        <ToggleButtonGroup
          value={[
            ...(aboutOrders ? ["orders"] : []),
            ...(aboutDeliveries ? ["deliveries"] : []),
            ...(hasPromotions ? ["promotions"] : []),
            ...(hasIndustryUpdates ? ["industry"] : []),
          ]}
          onChange={(_, vals: string[]) => {
            const nextOrders = vals.includes("orders");
            const nextDeliveries = vals.includes("deliveries");
            const lastChecked = vals.find((v) => !selectedVals.includes(v));
            const lastUnchecked = selectedVals.find((v) => !vals.includes(v));

            setAboutOrders(nextOrders);
            setAboutDeliveries(nextDeliveries);
            setHasPromotions(vals.includes("promotions"));
            setHasIndustryUpdates(vals.includes("industry"));

            setSelectedVals(vals);
            if (
              lastChecked === "promotions" ||
              lastChecked === "industry" ||
              lastUnchecked === "promotions" ||
              lastUnchecked === "industry"
            ) {
              return;
            }

            if (!nextDeliveries && aboutDeliveries) {
              setDelOutFor(false);
              setDelOutForMethod(MethodChoice.Email);
              setDelComplete(false);
              setDelCompleteMethod(MethodChoice.Email);
              setDelDaily(false);
              setDelDailyMethod(MethodChoice.Email);
            }

            if (nextOrders || nextDeliveries) {
              setShowCustomize(true);
              UpdateCheckBoxes({
                aboutOrders: nextOrders,
                aboutDeliveries: nextDeliveries,
              });
            }
          }}
          aria-label="topics"
          sx={{
            maxWidth: 640,
            width: "100%",
            mb: 3,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1.25,
            "& .MuiToggleButton-root": {
              width: "100%",
              justifyContent: "center",
              textTransform: "none",
              borderRadius: 1,
              borderColor: "#62b5e5",
              paddingBlock: 1.1,
              backgroundColor: "#fff",
              color: "inherit",
            },
            "& .MuiToggleButton-root.Mui-selected": {
              backgroundColor: "#62b5e5",
              color: "#fff",
              borderColor: "#62b5e5",
            },
            "& .MuiToggleButton-root.Mui-selected:hover": {
              backgroundColor: "#62b5e5",
            },
          }}
        >
          <ToggleButton value="orders">
            Orders{" "}
            {aboutOrders && (
              <CheckIcon
                fontSize="small"
                sx={{
                  position: "absolute",
                  right: 8,
                }}
              />
            )}
          </ToggleButton>
          <ToggleButton value="deliveries">
            Deliveries
            {aboutDeliveries && (
              <CheckIcon
                fontSize="small"
                sx={{
                  position: "absolute",
                  right: 8,
                }}
              />
            )}
          </ToggleButton>
          <ToggleButton value="promotions">
            Promotions
            {hasPromotions && (
              <CheckIcon
                fontSize="small"
                sx={{
                  position: "absolute",
                  right: 8,
                }}
              />
            )}
          </ToggleButton>
          <ToggleButton value="industry">
            Pricing Updates
            {hasIndustryUpdates && (
              <CheckIcon
                fontSize="small"
                sx={{
                  position: "absolute",
                  right: 8,
                }}
              />
            )}
          </ToggleButton>
        </ToggleButtonGroup>

        <FormControl component="fieldset" sx={{ width: "100%" }}>
          <FormLabel className="notification-subTitle">
            Contact Me By:
          </FormLabel>

          <Stack spacing={2} sx={{ mt: 1, mb: 4 }}>
            <Stack direction="row" alignItems="flex-start" spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={viaEmail}
                    onChange={(e) => {
                      const nextViaEmail = e.target.checked;
                      setViaEmail(nextViaEmail);
                      UpdateCheckBoxes({ viaEmail: nextViaEmail });
                    }}
                  />
                }
                label={
                  <Typography className="notification-title">Email</Typography>
                }
                sx={{ minWidth: 90 }}
              />

              {viaEmail && (
                <Box sx={{ flex: 1 }}>
                  {emails.map((val, i) => (
                    <Stack
                      key={i}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ flex: 1, maxWidth: 400, mb: 1 }}
                    >
                      <TextField
                        size="small"
                        fullWidth
                        type="email"
                        value={val}
                        onChange={(e) => updateEmailAt(i, e.target.value)}
                        onBlur={(e) => updateEmailAt(i, e.target.value.trim())}
                        error={!!val && !emailRegex.test(val)}
                      />
                      {emails.length > 1 && (
                        <IconButton
                          aria-label="remove email"
                          onClick={() => removeEmailAt(i)}
                          size="small"
                        >
                          <DeleteOutlineIcon sx={{ color: "#62b5e5" }} />
                        </IconButton>
                      )}
                    </Stack>
                  ))}

                  <Stack direction="row" alignItems="center">
                    <Button
                      size="small"
                      onClick={addEmail}
                      variant="text"
                      sx={{
                        textTransform: "none",
                        color: "#62b5e5",
                        fontWeight: 500,
                        minWidth: "auto",
                        p: 0,
                      }}
                    >
                      + Add New Email
                    </Button>
                  </Stack>
                </Box>
              )}
            </Stack>

            <Stack direction="row" alignItems="flex-start" spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={viaText}
                    onChange={(e) => {
                      const nextViaText = e.target.checked;
                      setViaText(nextViaText);
                      UpdateCheckBoxes({ viaText: nextViaText });
                    }}
                  />
                }
                label={
                  <Typography className="notification-title">Text</Typography>
                }
                sx={{ minWidth: 90 }}
              />

              {viaText && (
                <Box sx={{ flex: 1, maxWidth: 360 }}>
                  <TextField
                    size="small"
                    fullWidth
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhoneAt(e.target.value)}
                    inputProps={{
                      inputMode: "numeric",
                      maxLength: 12,
                      pattern: "\\d{3}-\\d{3}-\\d{4}",
                    }}
                  />
                </Box>
              )}
            </Stack>
          </Stack>
        </FormControl>

        {(aboutOrders || aboutDeliveries) && (
          <FormControl component="fieldset" sx={{ width: "100%" }}>
            <Typography className="notification-subTitle">
              How informed do you want to be:
            </Typography>
            <RadioGroup
              value={subscriptionType}
              onChange={(e) => {
                var subType = e.target
                  .value as OrderNotificationSubscriptionType;
                onChangeSubscriptionType(subType);
                if (subType == OrderNotificationSubscriptionType.DailySummary) {
                  setAckEach(false);
                  setReadyEach(false);

                  setAckDaily(true);
                  setReadyDaily(true);
                } else {
                  setAckDaily(false);
                  setReadyDaily(false);

                  setAckEach(true);
                  setReadyEach(true);
                }
              }}
              sx={{ mb: 2 }}
            >
              <FormControlLabel
                value={
                  OrderNotificationSubscriptionType.RecommendedNotifications
                }
                control={<Radio />}
                label={
                  <Typography className="notification-title">
                    Recommended Notifications
                  </Typography>
                }
              />
              <FormControlLabel
                value={OrderNotificationSubscriptionType.DailySummary}
                control={<Radio />}
                label={
                  <Typography className="notification-title">
                    Daily Summary
                  </Typography>
                }
              />
              <FormControlLabel
                value={OrderNotificationSubscriptionType.EachOrder}
                control={<Radio />}
                label={
                  <Typography className="notification-title">
                    Each Order
                  </Typography>
                }
              />
            </RadioGroup>
          </FormControl>
        )}

        {(aboutOrders || aboutDeliveries) && (
          <Accordion
            elevation={0}
            expanded={showCustomize}
            onChange={() => setShowCustomize((s) => !s)}
            sx={{
              mb: 2,
              boxShadow: "none",
              "&::before": { display: "none" },
            }}
          >
            <AccordionSummary
              sx={{
                backgroundColor: "#F3F3F3",
                mt: "20px",
                py: "18px",
                px: "20px",
                minHeight: "0px",
                width: "100%",
                "&.Mui-expanded": {
                  minHeight: "0px",
                },
                "& .MuiAccordionSummary-content": {
                  margin: 0,
                  my: 0,
                  "&.Mui-expanded": {
                    margin: 0,
                  },
                },
              }}
              expandIcon={<ExpandMoreIcon />}
            >
              <Typography className="customize-notification-subTitle">
                Customize My Notifications
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={4} mt={2}>
                {(aboutOrders || aboutDeliveries) && (
                  <>
                    <Box>
                      <Typography className="notification-subTitle">
                        Order &amp; Return Acknowledgments
                      </Typography>
                      <Box
                        sx={{ display: { xs: "none", sm: "none", md: "flex" } }}
                      >
                        <Typography
                          className="notification-title"
                          sx={{ mb: 0.5, fontWeight: 600, width: "100%" }}
                        >
                          Frequency
                        </Typography>
                        <Typography
                          className="notification-title"
                          sx={{ mb: 0.5, fontWeight: 600, width: "100%" }}
                        >
                          Choose Method
                        </Typography>
                      </Box>
                      <FrequencyMethodRow
                        label="Each Order"
                        checked={ackEach}
                        onCheckedChange={(v) => {
                          setAckEach(v);
                          if (!v) setAckEachMethod(MethodChoice.Email);
                          else {
                            setAboutOrders(true);
                          }
                          if (!readyDaily && !readyEach && !ackDaily && !v)
                            setAboutOrders(false);
                        }}
                        method={ackEachMethod}
                        onMethodChange={setAckEachMethod}
                        methodTestId="ack-each"
                      />
                      <FrequencyMethodRow
                        label="Daily Summary"
                        checked={ackDaily}
                        onCheckedChange={(v) => {
                          setAckDaily(v);
                          if (!v) setAckDailyMethod(MethodChoice.Email);
                          else {
                            setAboutOrders(true);
                          }
                          if (!readyDaily && !readyEach && !v && !ackEach)
                            setAboutOrders(false);
                        }}
                        method={ackDailyMethod}
                        onMethodChange={setAckDailyMethod}
                        methodTestId="ack-daily"
                      />
                    </Box>

                    <Box>
                      <Typography className="notification-subTitle">
                        Order Ready for Pickup/Delivery
                      </Typography>
                      <Box
                        sx={{ display: { xs: "none", sm: "none", md: "flex" } }}
                      >
                        <Typography
                          className="notification-title"
                          sx={{ mb: 0.5, fontWeight: 600, width: "100%" }}
                        >
                          Frequency
                        </Typography>
                        <Typography
                          className="notification-title"
                          sx={{ mb: 0.5, fontWeight: 600, width: "100%" }}
                        >
                          Choose Method
                        </Typography>
                      </Box>
                      <FrequencyMethodRow
                        label="Each Order"
                        checked={readyEach}
                        onCheckedChange={(v) => {
                          setReadyEach(v);
                          if (!v) setReadyEachMethod(MethodChoice.Email);
                          else {
                            setAboutOrders(true);
                          }
                          if (!readyDaily && !v && !ackDaily && !ackEach)
                            setAboutOrders(false);
                        }}
                        method={readyEachMethod}
                        onMethodChange={setReadyEachMethod}
                        methodTestId="ready-each"
                      />
                      <FrequencyMethodRow
                        label="Daily Summary"
                        checked={readyDaily}
                        onCheckedChange={(v) => {
                          setReadyDaily(v);
                          if (!v) setReadyDailyMethod(MethodChoice.Email);
                          else {
                            setAboutOrders(true);
                          }
                          if (!v && !readyEach && !ackDaily && !ackEach)
                            setAboutOrders(false);
                        }}
                        method={readyDailyMethod}
                        onMethodChange={setReadyDailyMethod}
                        methodTestId="ready-daily"
                      />
                    </Box>

                    <Box>
                      <Typography className="notification-subTitle">
                        Delivery Status Updates
                      </Typography>
                      <Box
                        sx={{ display: { xs: "none", sm: "none", md: "flex" } }}
                      >
                        <Typography
                          className="notification-title"
                          sx={{ mb: 0.5, fontWeight: 600, width: "100%" }}
                        >
                          Frequency
                        </Typography>
                        <Typography
                          className="notification-title"
                          sx={{ mb: 0.5, fontWeight: 600, width: "100%" }}
                        >
                          Choose Method
                        </Typography>
                      </Box>
                      <FrequencyMethodRow
                        label="Out For Delivery"
                        checked={delOutFor}
                        onCheckedChange={(v) => {
                          setDelOutFor(v);
                          if (!v) setDelOutForMethod(MethodChoice.Email);
                          else {
                            setAboutDeliveries(true);
                          }
                          if (!v && !delComplete && !delDaily)
                            setAboutDeliveries(false);
                        }}
                        method={delOutForMethod}
                        onMethodChange={setDelOutForMethod}
                        methodTestId="del-outfor"
                      />

                      <FrequencyMethodRow
                        label="Delivery Complete"
                        checked={delComplete}
                        onCheckedChange={(v) => {
                          setDelComplete(v);
                          if (!v) setDelCompleteMethod(MethodChoice.Email);
                          else {
                            setAboutDeliveries(true);
                          }
                          if (!delOutFor && !v && !delDaily)
                            setAboutDeliveries(false);
                        }}
                        method={delCompleteMethod}
                        onMethodChange={setDelCompleteMethod}
                        methodTestId="del-complete"
                      />

                      <FrequencyMethodRow
                        label="Daily Delivery Summary"
                        checked={delDaily}
                        onCheckedChange={(v) => {
                          setDelDaily(v);
                          if (!v) setDelDailyMethod(MethodChoice.Email);
                          else {
                            setAboutDeliveries(true);
                          }
                          if (!delOutFor && !delComplete && !v)
                            setAboutDeliveries(false);
                        }}
                        method={delDailyMethod}
                        onMethodChange={setDelDailyMethod}
                        methodTestId="del-daily"
                      />
                    </Box>
                  </>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}

        {!!errors.length && (
          <Typography color="error" sx={{ mb: 1 }} textAlign={"left"}>
            {errors.join(" ")}
          </Typography>
        )}

        {model?.hasShipTos && customerShipTos.length > 0 && (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography
              variant="body1"
              fontWeight={700}
              component="p"
              textAlign={"left"}
              sx={{ fontSize: { xs: 14, md: 16 } }}
            >
              Select the Ship To Accounts you'd like to receive informations
              for:
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Box className="dropdown-filter">
                <Autocomplete
                  multiple
                  fullWidth
                  size="small"
                  options={customerShipTos.flatMap((group) => group.options)}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                  value={selectedShipTos}
                  onChange={(_, newValue) => {
                    setSelectedShipTos(newValue as AddressDropdownItem[]);
                  }}
                  filterSelectedOptions
                  slotProps={{ popper: { sx: { zIndex: 2100 } } }}
                  renderTags={(value, getTagProps) =>
                    value.map((o, i) => (
                      <Chip
                        label={typeof o === "string" ? o : o.name}
                        variant="outlined"
                        {...getTagProps({ index: i })}
                        key={typeof o === "string" ? o : o.id}
                        sx={{ fontSize: { xs: 11, md: 14 } }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        selectedShipTos.length === 0
                          ? "All ship-tos currently selected"
                          : ""
                      }
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {shipToloading && <CircularProgress size={20} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      sx={{ fontSize: { xs: 11, md: 14 } }}
                    />
                  )}
                  ListboxProps={{
                    sx: {
                      fontSize: { xs: 11, md: 16 },
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="end"
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          {!isAccount && (
            <Button
              className="blueButtonOutline"
              size="large"
              onClick={handleContinueShopping}
            >
              CONTINUE SHOPPING
            </Button>
          )}

          <Button
            className="filledBlueButton blueButtonOutline"
            variant="contained"
            size="large"
            onClick={save}
            disabled={isSubscribed && !isDirty}
            sx={{
              backgroundColor: isSubscribed && !isDirty ? "#64A70B" : "#62b5e5",
              "&.Mui-disabled": {
                backgroundColor: "#64A70B !important",
                color: "#fff !important",
                opacity: 1,
              },
            }}
          >
            {isSubscribed && !isDirty ? "Updated" : "Update Preferences"}
          </Button>
        </Stack>
      </Paper>
    </>
  );
};

export const OrderNotificationPreferences = forwardRef<
  OrderNotificationPreferencesHandle,
  OrderNotificationPreferencesProps
>(OrderNotificationPreferencesInner);
